// src/pages/ProductsZeroStockPage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type StockOverride = "ORDERING" | null;

type Product = {
  id: number;
  code: string;
  name: string;

  // ยังมีมาจาก /productsListNew แต่หน้านี้ไม่ได้แสดงแล้ว (แสดงต้นทุนแทน)
  price: number;
  wholesalePrice: number;

  stockOnHand: number;
  stockStatusOverride?: StockOverride; // ✅ เพิ่ม

  unit?: string;
  createdAt?: string;
  imageUrl?: string;
  thumbUrl?: string;
};

type Movement = {
  id: number;
  product_code: string;
  qty: number;
  qty_sell: number;
  unit_cost: number;
  reason: "PURCHASE" | "SALE" | "ADJUST";
  ref_table: string | null;
  ref_no: string;
  created_at: string;
  status: string | null;
};

type Alert = { show: boolean; message: string; type?: "success" | "warning" | "info" | "danger" };
type Cost3 = { c1?: number; c2?: number; c3?: number };

function extractCodeNumber(code: string): number | null {
  const m = String(code).match(/(\d+)(?!.*\d)/);
  return m ? Number(m[1]) : null;
}

function thb(n?: number): string {
  if (typeof n !== "number" || Number.isNaN(n)) return "-";
  return n.toLocaleString("th-TH", { style: "currency", currency: "THB" });
}

function getErrMsg(payload: any, fallback: string): string {
  if (!payload) return fallback;
  if (typeof payload === "string") return payload;
  if (typeof payload === "object" && typeof payload.error === "string") return payload.error;
  return fallback;
}

export default function ProductsZeroStockPage(): JSX.Element {
  // ---------- Constants ----------
  const ZERO_PAGE_SIZE = 50;
  const SERVER_FETCH_LIMIT = 50;

  // ---------- UI State ----------
  const [toast, setToast] = useState<Alert>({ show: false, message: "", type: "success" });

  // search (ให้เหมือน ProductsListPage)
  const [q, setQ] = useState<string>("");
  const [qInput, setQInput] = useState<string>("");

  // zero pagination
  const [zeroPage, setZeroPage] = useState<number>(1);

  // loading / error
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // cache rows (สะสมของหมดให้ครบ 50 ต่อหน้า)
  const [zeroRows, setZeroRows] = useState<Product[]>([]);
  const [serverCursorPage, setServerCursorPage] = useState<number>(1);
  const [serverTotal, setServerTotal] = useState<number>(0);

  // Movement modal
  const [showMovModal, setShowMovModal] = useState(false);
  const [movLoading, setMovLoading] = useState(false);
  const [movError, setMovError] = useState<string>("");
  const [movItems, setMovItems] = useState<Movement[]>([]);
  const [movCode, setMovCode] = useState<string>("");
  const [movLimit] = useState<number>(50);
  const [movOffset, setMovOffset] = useState<number>(0);

  // Abort
  const productsAbortRef = useRef<AbortController | null>(null);

  // cost cache (3 lot ล่าสุด)
  const [costByCode, setCostByCode] = useState<Record<string, Cost3>>({});
  const [costLoadingByCode, setCostLoadingByCode] = useState<Record<string, boolean>>({});
  const costByCodeRef = useRef<Record<string, Cost3>>({});
  useEffect(() => {
    costByCodeRef.current = costByCode;
  }, [costByCode]);

  // ✅ action loading per row (ORDERING / CANCEL)
  const [actionLoadingByCode, setActionLoadingByCode] = useState<Record<string, boolean>>({});

  // ---------- Toast auto-hide ----------
  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast({ show: false, message: "" }), 2200);
    return () => clearTimeout(t);
  }, [toast.show]);

  // ---------- Helpers ----------
  const sortProducts = (list: Product[]): Product[] => {
    return [...list].sort((a, b) => {
      const na = extractCodeNumber(a.code);
      const nb = extractCodeNumber(b.code);
  
      // ✅ มาก -> น้อย
      if (na != null && nb != null && na !== nb) return nb - na;
  
      // เลือกนโยบาย: อันที่มีตัวเลขให้มาก่อน
      if (na != null && nb == null) return -1;
      if (na == null && nb != null) return 1;
  
      // fallback
      return b.code.localeCompare(a.code, "th-TH", { numeric: true, sensitivity: "base" }); // มาก->น้อย
    });
  };

  const resetZeroContext = () => {
    setZeroRows([]);
    setServerCursorPage(1);
    setServerTotal(0);
    setZeroPage(1);

    setCostByCode({});
    setCostLoadingByCode({});
    setActionLoadingByCode({});
  };

  // ---------- Fetch one server page ----------
  const fetchServerPage = async (
    query: string | undefined,
    pageToFetch: number
  ): Promise<{ list: Product[]; total: number }> => {
    const qs: string[] = [];
    if (query && query.trim()) qs.push(`q=${encodeURIComponent(query.trim())}`);
    qs.push(`page=${pageToFetch}`);
    qs.push(`limit=${SERVER_FETCH_LIMIT}`);

    const url = apiUrl(`/productsListNew?${qs.join("&")}`);

    productsAbortRef.current?.abort();
    const ac = new AbortController();
    productsAbortRef.current = ac;

    const resp = await fetch(url, { signal: ac.signal });
    const json = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(json?.error ?? resp.statusText);

    const list: Product[] = (json.items ?? []).map((r: any) => ({
      id: Number(r.id),
      code: String(r.code ?? ""),
      name: String(r.name ?? ""),

      price: Number(r.price ?? 0),
      wholesalePrice: Number(r.wholesalePrice ?? 0),

      stockOnHand: Number(r.stockOnHand ?? 0),
      stockStatusOverride: (r.stockStatusOverride ?? null) as StockOverride,

      unit: r.unit ?? undefined,
      createdAt: r.createdAt ?? undefined,
      imageUrl: r.imageUrl ?? undefined,
      thumbUrl: r.thumbUrl ?? r.imageUrl ?? undefined,
    }));

    return { list, total: Number(json.total ?? 0) };
  };

  // ---------- Ensure we have enough zero rows for page ----------
  // PMN-CRITICAL(/ProductsZeroStockPage): /productsListNew returns all products; this loop client-filters stockOnHand === 0 until the zero-stock page is filled.
  const ensureZeroRowsForPage = async (
    targetZeroPage: number,
    query: string | undefined,
    seedAcc: Product[],
    seedCursorPage: number
  ): Promise<{ acc: Product[]; cursorPage: number; total: number }> => {
    const need = targetZeroPage * ZERO_PAGE_SIZE;
  
    let cursor = seedCursorPage;
    let acc = [...seedAcc];
    let total = serverTotal;
  
    while (acc.length < need) {
      const { list, total: t } = await fetchServerPage(query, cursor);
      total = t;
  
      const onlyZero = list.filter((p) => Number(p.stockOnHand) === 0);
      acc = acc.concat(onlyZero);
  
      if (cursor * SERVER_FETCH_LIMIT >= total) break;
      cursor += 1;
    }
  
    return { acc, cursorPage: cursor, total };
  };

  // ---------- Derived: current page items ----------
  const zeroPageItems = useMemo(() => {
    const start = (zeroPage - 1) * ZERO_PAGE_SIZE;
    return zeroRows.slice(start, start + ZERO_PAGE_SIZE);
  }, [zeroRows, zeroPage]);

  const canGoPrev = zeroPage > 1 && !loading;

  const canGoNext = useMemo(() => {
    if (loading) return false;
    const start = (zeroPage - 1) * ZERO_PAGE_SIZE;
    const alreadyHaveNext = zeroRows.length > start + ZERO_PAGE_SIZE;
    const mayHaveMoreOnServer = serverTotal === 0 ? true : serverCursorPage * SERVER_FETCH_LIMIT < serverTotal;
    return alreadyHaveNext || mayHaveMoreOnServer;
  }, [loading, zeroPage, zeroRows.length, serverCursorPage, serverTotal]);

  // ---------- Init load ----------
  useEffect(() => {
    // init: โหลดหน้าแรกแบบผ่าน runSearch (จะไม่ติด stale state)
    void (async () => {
      setZeroPage(1);
      await runSearch(undefined, 1);
    })();
  
    return () => {
      productsAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Costs: fetch last 3 PURCHASE unit_cost ----------
  // PMN-CRITICAL(/ProductsZeroStockPage): These are latest PURCHASE movement costs, not FIFO/current available costs.
  const fetchLast3PurchaseCosts = async (code: string): Promise<Cost3> => {
    const url = apiUrl(`/inventory_movements?product_code=${encodeURIComponent(code)}&limit=200&offset=0`);
    const resp = await fetch(url);
    const j = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(j?.error ?? resp.statusText);

    const items: any[] = Array.isArray(j?.items) ? j.items : [];

    const purchases = items
      .filter((r) => String(r.reason || "").toUpperCase() === "PURCHASE")
      .sort((a, b) => new Date(String(b.created_at)).getTime() - new Date(String(a.created_at)).getTime())
      .slice(0, 3);

    const c1 = purchases[0]?.unit_cost != null ? Number(purchases[0].unit_cost) : undefined;
    const c2 = purchases[1]?.unit_cost != null ? Number(purchases[1].unit_cost) : undefined;
    const c3 = purchases[2]?.unit_cost != null ? Number(purchases[2].unit_cost) : undefined;

    return { c1, c2, c3 };
  };

  // ---------- Prefetch costs for current page (limited concurrency) ----------
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const codes = zeroPageItems.map((p) => p.code).filter(Boolean);

      const CONCURRENCY = 6;
      let idx = 0;

      const worker = async () => {
        while (idx < codes.length) {
          const code = codes[idx++];
          if (cancelled) return;

          if (costByCodeRef.current[code]) continue;

          setCostLoadingByCode((m) => ({ ...m, [code]: true }));
          try {
            const c = await fetchLast3PurchaseCosts(code);
            if (cancelled) return;
            setCostByCode((m) => ({ ...m, [code]: c }));
          } catch {
            if (cancelled) return;
            setCostByCode((m) => ({ ...m, [code]: {} }));
          } finally {
            if (!cancelled) setCostLoadingByCode((m) => ({ ...m, [code]: false }));
          }
        }
      };

      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, codes.length) }, () => worker()));
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [zeroPageItems]);

  // ---------- Search/refresh flow (เหมือน ProductsListPage) ----------
  
  const runSearch = async (query: string | undefined, targetPage: number) => {
    setLoading(true);
    setErrorMsg("");
  
    // reset states ก่อน (แต่ไม่พึ่งมัน)
    setZeroRows([]);
    setServerCursorPage(1);
    setServerTotal(0);
    setCostByCode({});
    setCostLoadingByCode({});
    setActionLoadingByCode({});
  
    try {
      const { acc, cursorPage, total } = await ensureZeroRowsForPage(targetPage, query, [], 1);
      setZeroRows(sortProducts(acc));
      setServerCursorPage(cursorPage);
      setServerTotal(total);
    } catch (err: any) {
      if (String(err?.name) === "AbortError") return;
      const msg = err?.message ?? "โหลดรายการสินค้าไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  const doSearch = async () => {
    const qVal = qInput.trim();
    setQ(qVal);
    setZeroPage(1);
    await runSearch(qVal || undefined, 1);
  };

  const refresh = async () => {
    const keepPage = zeroPage;
    await runSearch(q || undefined, keepPage);
    setZeroPage(keepPage);
  };

  const goNext = async () => {
    const next = zeroPage + 1;
    setZeroPage(next);
  
    setLoading(true);
    setErrorMsg("");
    try {
      const { acc, cursorPage, total } = await ensureZeroRowsForPage(next, q || undefined, zeroRows, serverCursorPage);
      setZeroRows(sortProducts(acc));
      setServerCursorPage(cursorPage);
      setServerTotal(total);
    } catch (err: any) {
      if (String(err?.name) === "AbortError") return;
      const msg = err?.message ?? "โหลดรายการสินค้าไม่สำเร็จ";
      setErrorMsg(msg);
      setToast({ show: true, message: msg, type: "danger" });
    } finally {
      setLoading(false);
    }
  };
  
  const goPrev = async () => {
    setZeroPage((p) => Math.max(p - 1, 1));
  };

  // ---------- Row actions: ORDERING / CANCEL ----------
  // PMN-CRITICAL(/ProductsZeroStockPage): Backend only allows ORDERING when products.stock_on_hand = 0; 409 means refresh and trust server state.
  const setOrdering = async (code: string) => {
    const safe = code.trim();
    if (!safe) return;

    setActionLoadingByCode((m) => ({ ...m, [safe]: true }));
    try {
      const url = apiUrl(`/products/${encodeURIComponent(safe)}/ordering`);
      const resp = await fetch(url, { method: "POST" });
      const payload = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        const emsg = getErrMsg(payload, resp.statusText);

        // ถ้า stock ไม่เป็น 0 แล้ว => sync ใหม่
        if (resp.status === 409) {
          setToast({ show: true, message: "สินค้าไม่อยู่สถานะของหมดแล้ว — รีเฟรชข้อมูลให้ใหม่", type: "warning" });
          await refresh();
          return;
        }

        throw new Error(emsg);
      }

      // อัปเดตเฉพาะ row ใน cache ให้เห็นทันที
      setZeroRows((rows) =>
        rows.map((p) => (p.code === safe ? { ...p, stockStatusOverride: "ORDERING" } : p))
      );

      setToast({ show: true, message: `ตั้งสถานะ “กำลังสั่ง” แล้ว: ${safe}`, type: "success" });
    } catch (err: any) {
      setToast({ show: true, message: `ตั้ง “กำลังสั่ง” ไม่สำเร็จ: ${String(err?.message || "failed")}`, type: "danger" });
    } finally {
      setActionLoadingByCode((m) => ({ ...m, [safe]: false }));
    }
  };

  const cancelOrdering = async (code: string) => {
    const safe = code.trim();
    if (!safe) return;

    setActionLoadingByCode((m) => ({ ...m, [safe]: true }));
    try {
      const url = apiUrl(`/products/${encodeURIComponent(safe)}/ordering_cancel`);
      const resp = await fetch(url, { method: "POST" });
      const payload = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        const emsg = getErrMsg(payload, resp.statusText);
        throw new Error(emsg);
      }

      setZeroRows((rows) =>
        rows.map((p) => (p.code === safe ? { ...p, stockStatusOverride: null } : p))
      );

      setToast({ show: true, message: `ยกเลิก “กำลังสั่ง” แล้ว: ${safe}`, type: "success" });
    } catch (err: any) {
      setToast({ show: true, message: `ยกเลิกไม่สำเร็จ: ${String(err?.message || "failed")}`, type: "danger" });
    } finally {
      setActionLoadingByCode((m) => ({ ...m, [safe]: false }));
    }
  };

  // ---------- Movement ----------
  const fetchMovements = async (code: string, offset: number) => {
    try {
      setMovLoading(true);
      setMovError("");

      const url = apiUrl(`/inventory_movements?product_code=${encodeURIComponent(code)}&limit=${movLimit}&offset=${offset}`);

      const resp = await fetch(url);
      const ct = resp.headers.get("content-type") || "";
      const isJson = ct.includes("application/json");
      const payload = isJson ? await resp.json().catch(() => ({})) : await resp.text();

      if (!resp.ok) {
        const msg = isJson ? (payload?.error ?? resp.statusText) : `HTTP ${resp.status} — ${String(payload).slice(0, 120)}`;
        throw new Error(msg);
      }

      const list: Movement[] = (payload.items ?? []).map((r: any) => ({
        id: Number(r.id),
        product_code: String(r.product_code ?? ""),
        qty: Number(r.qty ?? 0),
        qty_sell: Number(r.qty_sell ?? 0),
        unit_cost: Number(r.unit_cost ?? 0),
        reason: r.reason,
        ref_table: r.ref_table ?? null,
        ref_no: r.ref_no ?? "-",
        created_at: String(r.created_at ?? ""),
        status: r.status ?? null,
      }));

      setMovItems(list);
    } catch (err: any) {
      setMovError(err?.message ?? "โหลดประวัติไม่สำเร็จ");
    } finally {
      setMovLoading(false);
    }
  };

  const openMovements = async (code: string) => {
    setMovCode(code);
    setMovItems([]);
    setMovError("");
    setMovOffset(0);
    setShowMovModal(true);
    await fetchMovements(code, 0);
  };

  const nextMovPage = () => {
    const newOffset = movOffset + movLimit;
    setMovOffset(newOffset);
    void fetchMovements(movCode, newOffset);
  };

  const prevMovPage = () => {
    const newOffset = Math.max(movOffset - movLimit, 0);
    setMovOffset(newOffset);
    void fetchMovements(movCode, newOffset);
  };

  // ---------- UI ----------
  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      {/* Toast */}
      <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
        {toast.show && (
          <div className={`alert alert-${toast.type ?? "success"} shadow mb-0 rounded-3 py-2 px-3`}>{toast.message}</div>
        )}
      </div>

      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-9">
            <div className="card shadow border-0 rounded-4">
              <div className="card-body p-4 p-md-5">
                {/* Header */}
                <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                  <div>
                    <h4 className="mb-1 text-danger">สินค้าของหมด (Stock = 0)</h4>
                    <div className="small text-muted">แสดงเฉพาะรายการคงเหลือ = 0 (50 รายการ/หน้า) + แสดงต้นทุน 3 lot ล่าสุด</div>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <Link to="/ProductsAddPage" className="btn btn-success btn-sm d-inline-flex align-items-center gap-2">
                      <span className="fw-bold">+</span> เพิ่มสินค้าใหม่
                    </Link>
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={refresh} disabled={loading}>
                      รีเฟรช
                    </button>
                  </div>
                </div>

                {/* Search (ให้เหมือน ProductsListPage) */}
                <div className="input-group mb-3">
                  <input
                    className="form-control"
                    placeholder="ค้นหาโค้ด/ชื่อ/หน่วย (กด Enter เพื่อค้นหา server)"
                    value={qInput}
                    onChange={(e) => setQInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && void doSearch()}
                  />
                  <button className="btn btn-primary" type="button" onClick={() => void doSearch()} disabled={loading}>
                    ค้นหา
                  </button>
                </div>

                {/* Status */}
                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span>กำลังโหลด…</span>
                  </div>
                )}
                {errorMsg && (
                  <div className="alert alert-warning rounded-3" role="alert">
                    โหลดไม่สำเร็จ: {errorMsg}
                  </div>
                )}

                {/* Table */}
                <div className="border rounded-3 p-0 bg-white">
                  <div className="table-responsive" style={{ maxHeight: 560 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light sticky-top">
                        <tr>
                          <th style={{ width: 76 }}>รูป</th>
                          <th style={{ width: 160 }}>โค้ด</th>
                          <th>ชื่อสินค้า</th>

                          <th style={{ width: 120 }} className="text-end">ต้นทุนล่าสุด</th>
                          <th style={{ width: 120 }} className="text-end">ต้นทุน #2</th>
                          <th style={{ width: 120 }} className="text-end">ต้นทุน #3</th>

                          <th style={{ width: 110 }} className="text-end">สถานะ</th>
                          <th style={{ width: 260 }} className="text-end">จัดการ</th>
                        </tr>
                      </thead>

                      <tbody>
                        {zeroPageItems.length === 0 && !loading && (
                          <tr>
                            <td colSpan={8} className="text-center text-muted py-4">
                              ไม่พบสินค้าของหมด
                              {q ? <div className="small mt-1">คำค้น: “{q}”</div> : null}
                            </td>
                          </tr>
                        )}

                        {zeroPageItems.map((p) => {
                          const thumbSrc = p.thumbUrl || p.imageUrl || null;
                          const fullSrc = p.imageUrl || thumbSrc || null;

                          const cost = costByCode[p.code];
                          const isCostLoading = !!costLoadingByCode[p.code];

                          const isOrdering = p.stockStatusOverride === "ORDERING";
                          const isActionLoading = !!actionLoadingByCode[p.code];

                          return (
                            <tr key={p.id} className="user-select-none">
                              <td>
                                {thumbSrc ? (
                                  <img
                                    src={thumbSrc}
                                    alt={p.name}
                                    width={56}
                                    height={56}
                                    loading="lazy"
                                    decoding="async"
                                    style={{ objectFit: "cover", borderRadius: 8, background: "#f5f5f5" }}
                                    onMouseEnter={() => setPreviewImage(fullSrc)}
                                    onMouseLeave={() => setPreviewImage(null)}
                                    onError={(e) => {
                                      (e.currentTarget as HTMLImageElement).style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <div className="bg-light border rounded" style={{ width: 56, height: 56 }} />
                                )}
                              </td>

                              <td className="fw-semibold">{p.code}</td>
                              <td>{p.name}</td>

                              <td className="text-end">{isCostLoading ? "…" : thb(cost?.c1)}</td>
                              <td className="text-end">{isCostLoading ? "…" : thb(cost?.c2)}</td>
                              <td className="text-end">{isCostLoading ? "…" : thb(cost?.c3)}</td>

                              <td className="text-end">
                                {isOrdering ? (
                                  <span className="badge text-bg-warning">กำลังสั่ง</span>
                                ) : (
                                  <span className="badge text-bg-danger">ของหมด</span>
                                )}
                              </td>

                              <td className="text-end">
                                <div className="d-inline-flex gap-2">
                                  {!isOrdering ? (
                                    <button
                                      type="button"
                                      className="btn btn-warning btn-sm"
                                      disabled={isActionLoading || loading}
                                      onClick={() => void setOrdering(p.code)}
                                      title="ตั้งสถานะเป็นกำลังสั่ง"
                                    >
                                      {isActionLoading ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-1" />
                                          กำลังตั้ง…
                                        </>
                                      ) : (
                                        "กำลังสั่ง"
                                      )}
                                    </button>
                                  ) : (
                                    <button
                                      type="button"
                                      className="btn btn-outline-warning btn-sm"
                                      disabled={isActionLoading || loading}
                                      onClick={() => void cancelOrdering(p.code)}
                                      title="ยกเลิกสถานะกำลังสั่ง"
                                    >
                                      {isActionLoading ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-1" />
                                          กำลังยกเลิก…
                                        </>
                                      ) : (
                                        "ยกเลิก"
                                      )}
                                    </button>
                                  )}

                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => void openMovements(p.code)}
                                    disabled={loading}
                                  >
                                    ประวัติ
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Footer */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    ของหมดหน้า {zeroPage}: {zeroPageItems.length} รายการ
                  </div>

                  <div className="btn-group btn-group-sm">
                    <button type="button" className="btn btn-outline-secondary" disabled={!canGoPrev} onClick={() => void goPrev()}>
                      ← ก่อนหน้า
                    </button>
                    <button type="button" className="btn btn-outline-secondary" disabled={!canGoNext} onClick={() => void goNext()}>
                      ถัดไป →
                    </button>
                  </div>
                </div>

                <a href="/" className="btn btn-link text-decoration-none flex-fill text-center mt-2">
                  ← กลับหน้าเมนู
                </a>
              </div>
            </div>

            {/* Hover preview */}
            {previewImage && (
              <div
                className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                style={{ backgroundColor: "rgba(0,0,0,0.7)", zIndex: 2000, pointerEvents: "none" }}
              >
                <img
                  src={previewImage}
                  alt="preview"
                  style={{
                    maxWidth: "90%",
                    maxHeight: "90%",
                    borderRadius: 12,
                    boxShadow: "0 0 20px rgba(0,0,0,0.5)",
                    background: "#fff",
                    opacity: 1,
                  }}
                />
              </div>
            )}

            {/* Movement modal */}
            {showMovModal && (
              <div
                className="modal fade show"
                role="dialog"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,.5)" }}
                aria-modal="true"
                aria-labelledby="movModalLabel"
                onClick={(e) => {
                  if (e.target === e.currentTarget) setShowMovModal(false);
                }}
              >
                <div className="modal-dialog modal-lg modal-dialog-scrollable">
                  <div className="modal-content rounded-4 shadow">
                    <div className="modal-header">
                      <h5 className="modal-title" id="movModalLabel">
                        ประวัติความเคลื่อนไหว: {movCode}
                      </h5>
                      <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowMovModal(false)} />
                    </div>

                    <div className="modal-body">
                      {movLoading && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="spinner-border spinner-border-sm" role="status" />
                          <span>กำลังโหลดประวัติ…</span>
                        </div>
                      )}
                      {movError && (
                        <div className="alert alert-warning rounded-3" role="alert">
                          โหลดประวัติไม่สำเร็จ: {movError}
                        </div>
                      )}

                      <div className="table-responsive" style={{ maxHeight: 420 }}>
                        <table className="table table-sm table-hover align-middle mb-0">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th style={{ width: 160 }}>วันที่</th>
                              <th style={{ width: 110 }}>เหตุผล</th>
                              <th className="text-end" style={{ width: 100 }}>
                                รับเข้า (+)
                              </th>
                              <th className="text-end" style={{ width: 100 }}>
                                จ่ายออก (-)
                              </th>
                              <th className="text-end" style={{ width: 130 }}>
                                ต้นทุน/หน่วย
                              </th>
                              <th>อ้างอิง</th>
                            </tr>
                          </thead>
                          <tbody>
                            {movItems.length === 0 && !movLoading && (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-3">
                                  ไม่มีข้อมูล
                                </td>
                              </tr>
                            )}

                            {movItems.map((m) => (
                              <tr
                                key={m.id}
                                style={m.status === "sold_out" ? { backgroundColor: "rgba(25, 135, 84, 0.12)" } : undefined}
                                title={m.status === "sold_out" ? "ขายหมด (sold out)" : undefined}
                              >
                                <td>{new Date(m.created_at).toLocaleString("th-TH", { timeZone: "Asia/Bangkok" })}</td>
                                <td>{m.reason === "PURCHASE" ? "รับเข้า" : m.reason === "SALE" ? "ขายออก" : "ปรับสต็อก"}</td>
                                <td className="text-end">{m.qty > 0 ? m.qty : "-"}</td>
                                <td className="text-end">{m.qty_sell > 0 ? m.qty_sell : "-"}</td>
                                <td className="text-end">{thb(m.unit_cost)}</td>
                                <td>
                                  {m.ref_table || "-"} {m.ref_no ? `#${m.ref_no}` : ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="modal-footer d-flex justify-content-between">
                      <div className="btn-group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={movOffset === 0 || movLoading}
                          onClick={prevMovPage}
                        >
                          ← ก่อนหน้า
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={movItems.length < movLimit || movLoading}
                          onClick={nextMovPage}
                        >
                          ถัดไป →
                        </button>
                      </div>
                      <button type="button" className="btn btn-secondary" onClick={() => setShowMovModal(false)}>
                        ปิด
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* /Movement modal */}
          </div>
        </div>
      </div>
    </div>
  );
}
